/**
 * Papier Royal — Customily Bridge (v2 — real keys confirmed 2026-05-15)
 *
 * Capture l'état de personnalisation Customily d'un produit (ex : business card)
 * et le rejoue sur un companion (ex : envelope).
 *
 * Architecture Customily observée :
 *   - Main item : a `_customily-upsell-main-id` dans ses properties + l'option
 *     personnalisée sous une clé dynamique (ex : "Make It Your Own: +€5 per character"
 *     → "ABC"). Plus quelques métadonnées : Position, _customily-preview,
 *     _customily-thumb, _customily-personalization-id, _customily-customization-url,
 *     _customily-production-url, _customily-eps-name, _customily-thumb-id.
 *   - Upsell item : produit séparé (ex : `dorure-gilding`) avec quantity = nb chars,
 *     properties `_customily-option-name` + `_customily-upsell-id` + `_customily-upsell-multiplier`.
 *
 * Stratégie de sync :
 *   - On extrait les chars + position + preview URL du main item
 *   - On les rejoue dans les line item properties du companion (best-effort —
 *     Customily ne pré-fillera pas le configurateur sur le companion sans
 *     Selling Groups configurés côté Customily, mais la production aura les
 *     bonnes valeurs et le visuel preview est cohérent)
 *   - Sur le thumbnail du companion, on affiche soit le preview Customily du
 *     main (image), soit un fallback SVG monogramme (si pas de preview URL)
 */

(function () {
  'use strict';

  // Métadonnées Customily techniques (tirets, pas underscores — confirmé 2026-05-15)
  const META_KEYS = {
    upsellMainId: '_customily-upsell-main-id',
    upsellId: '_customily-upsell-id',
    personalizationId: '_customily-personalization-id',
    previewUrl: '_customily-preview',
    thumbUrl: '_customily-thumb',
    customizationUrl: '_customily-customization-url',
    productionUrl: '_customily-production-url',
    epsName: '_customily-eps-name',
    thumbId: '_customily-thumb-id'
  };

  // Properties à NE PAS recopier sur le companion (techniques, spécifiques au main)
  const STRIP_KEYS = new Set([
    META_KEYS.upsellMainId,
    META_KEYS.personalizationId,
    META_KEYS.thumbId,
    META_KEYS.epsName,
    META_KEYS.productionUrl,
    META_KEYS.customizationUrl,
    META_KEYS.thumbUrl,
    // On garde _customily-preview pour afficher le visuel sur le companion thumbnail
  ]);

  // Properties HUMAINES qu'on NE veut PAS hériter sur le companion.
  // Confirmé 2026-05-17 : business card et envelope partagent les mêmes layer IDs 7-17.
  // Donc on PARTAGE tout sauf les keys explicitement product-specific (Position,
  // Layout, Orientation). Les layer IDs / Monogram IDs / Size IDs sont partagés.
  const STRIP_HUMAN_PATTERNS = [
    /^position$/i,
    /^placement$/i,
    /^emplacement$/i,
    /^layout$/i,
    /^disposition$/i,
    /^orientation$/i,
  ];

  // Patterns qui IDENTIFIENT les champs partageables (text/font/color).
  // On extrait leur valeur peu importe comment la property est nommée par Customily.
  // L'ordre importe : on prend la 1ère valeur qui match.
  const SHARE_FIELD_PATTERNS = {
    text: [/text$/i, /^texte/i, /^chars?$/i, /^caract[èe]re/i, /^initiales?$/i, /^initials$/i, /make it your own/i],
    font: [/font/i, /^police/i, /^calligraphie/i, /typeface/i],
    color: [/text color$/i, /^color$/i, /^couleur$/i, /^encre$/i, /^ink$/i],
  };

  function isStrippedHumanKey(key) {
    return STRIP_HUMAN_PATTERNS.some((rx) => rx.test(key));
  }

  /**
   * Extrait les 3 valeurs canoniques (text, font, color) depuis les properties humaines.
   *
   * Pass 1 : tente l'extraction par NOM de key (regex sur SHARE_FIELD_PATTERNS).
   * Pass 2 (fallback) : si Pass 1 a tout raté ET qu'on détecte des keys
   *   whitespace (Customily cache souvent les libellés via espaces +
   *   zero-width chars), on extrait par POSITION en triant ces keys par
   *   longueur — qui correspond à l'ordre des layers Customily.
   *   Convention observée Papier Royal :
   *     1ère key (la plus courte) → initiales
   *     2e key                    → police / série
   *     3e key                    → couleur
   *     4e+ (adresse / address font / address color) → non extrait ici,
   *     mais passé via le passthrough de serializeForLineItem.
   */
  function extractMonogramFields(customization) {
    const found = { text: null, font: null, color: null };

    // Pass 1 : extraction par nom de key (cas standard Customily)
    for (const [key, value] of Object.entries(customization)) {
      if (!value || isStrippedHumanKey(key)) continue;
      for (const [field, patterns] of Object.entries(SHARE_FIELD_PATTERNS)) {
        if (found[field]) continue;
        if (patterns.some((rx) => rx.test(key))) {
          found[field] = value;
          break;
        }
      }
    }

    // Pass 2 : fallback position-based pour les keys 100% whitespace.
    // Regex couvre espaces standards + zero-width chars U+200B..U+200D + BOM U+FEFF
    // (Customily compose ses keys "invisibles" avec ces caractères).
    if (!found.text && !found.font && !found.color) {
      const isWhitespaceKey = (key) => /^[\s\u200B\u200C\u200D\uFEFF]+$/.test(key);
      const whitespaceEntries = Object.entries(customization)
        .filter(([key, value]) => value && isWhitespaceKey(key))
        .sort(([a], [b]) => a.length - b.length);
      if (whitespaceEntries[0]) found.text = whitespaceEntries[0][1];
      if (whitespaceEntries[1]) found.font = whitespaceEntries[1][1];
      if (whitespaceEntries[2]) found.color = whitespaceEntries[2][1];
    }

    return found;
  }

  const KNOWN_FONTS = {
    cassandre: { family: '"Cassandre Tribute", "Armorial", serif', weight: 400 },
    copiepa: { family: '"Copiépa", "Bricolage Grotesque", sans-serif', weight: 500 },
    vendochin: { family: '"Vendochin", "Fraunces", serif', weight: 400 }
  };

  /**
   * Capture le design depuis le cart pour un handle donné.
   * Identifie le "main item" en filtrant les upsells Customily.
   * Retourne null si pas de personnalisation détectée.
   */
  async function captureFromCart(initiatorHandle) {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      // Trouve la dernière occurrence (= plus récente) du produit main, filtré
      // des upsells Customily.
      // IMPORTANT : /cart.js renvoie cart.items en ordre REVERSE-chronologique
      // (most recent FIRST), donc le dernier ajout = matches[0], NOT matches[length-1].
      // Bug observé 2026-05-19 : multi-sessions du business-card → on persistait
      // la perso de la PLUS ANCIENNE session au lieu de la plus récente.
      const matches = cart.items.filter((item) => {
        const props = item.properties || {};
        return item.handle === initiatorHandle && !props[META_KEYS.upsellId];
      });
      const item = matches[0];
      if (!item) return null;

      const design = extractDesign(item.properties || {});
      if (!hasMinimumDesign(design)) return null;

      return design;
    } catch (e) {
      console.warn('[PR Customily bridge] cart capture failed', e);
      return null;
    }
  }

  /**
   * Sépare les properties en deux : techniques (_customily-*) et personnalisations
   * (clés "humaines" comme "Make It Your Own: +€5 per character", "Position").
   */
  function extractDesign(properties) {
    const technical = {};
    const customization = {}; // ce que le user a effectivement personnalisé

    Object.entries(properties).forEach(([key, value]) => {
      if (key.startsWith('_customily-')) {
        technical[key] = value;
      } else if (!key.startsWith('_')) {
        // Properties humaines (visibles dans le récap panier)
        customization[key] = value;
      }
    });

    // Extraction smart des champs monogramme canoniques (text/font/color)
    // peu importe comment Customily les a nommés dans ce template.
    const monogram = extractMonogramFields(customization);
    const position = customization['Position'] || customization['position'] || null;

    return {
      technical,
      customization,
      // Champs dérivés pour l'usage downstream (rendu + serialize)
      primaryText: monogram.text, // ex: "AG" — pour le SVG fallback
      font: monogram.font, // ex: "Cassandre"
      color: monogram.color, // ex: "Noir"
      position, // ex: "Bottom Right" — capturé mais PAS partagé au companion
      previewUrl: technical[META_KEYS.previewUrl] || null,
      thumbUrl: technical[META_KEYS.thumbUrl] || null,
      personalizationId: technical[META_KEYS.personalizationId] || null,
      upsellMainId: technical[META_KEYS.upsellMainId] || null
    };
  }

  function hasMinimumDesign(design) {
    return !!(design && (design.primaryText || design.previewUrl || design.personalizationId));
  }

  /**
   * Rend un preview sur le thumbnail du companion.
   * Stratégie : si on a une preview URL Customily, on l'affiche en overlay.
   * Sinon, fallback SVG monogramme avec les chars normalisés.
   */
  function renderPreview(slot, design) {
    if (!slot || !design) return;

    // Option A : preview Customily existante
    if (design.previewUrl) {
      slot.innerHTML = '';
      const img = document.createElement('img');
      img.src = design.previewUrl;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.className = 'pr-companion__preview-img';
      slot.appendChild(img);
      slot.classList.add('pr-companion__preview--customily');
      return;
    }

    // Option B : fallback SVG monogramme à partir du primaryText
    const text = String(design.primaryText || '').toUpperCase().slice(0, 4);
    if (!text) return;

    // Pick font from the inherited choice (best-effort match)
    const fontKey = String(design.font || 'cassandre').toLowerCase().replace(/[^a-z]/g, '');
    const font = KNOWN_FONTS[fontKey] || KNOWN_FONTS.cassandre;
    const color = isValidColor(design.color) ? design.color : '#1a1a1a';

    slot.innerHTML = '';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 120 120');
    svg.setAttribute('class', 'pr-companion__monogram');
    svg.setAttribute('aria-hidden', 'true');

    const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    t.setAttribute('x', '60');
    t.setAttribute('y', '72');
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('font-family', font.family);
    t.setAttribute('font-weight', String(font.weight));
    t.setAttribute('font-size', text.length > 2 ? '34' : '46');
    t.setAttribute('fill', color);
    t.setAttribute('letter-spacing', '2');
    t.textContent = text;
    svg.appendChild(t);

    slot.appendChild(svg);
    slot.classList.add('pr-companion__preview--monogram');
  }

  /**
   * Sérialise le design en line item properties pour l'add-to-cart du companion.
   *
   * IMPORTANT : Customily ne pré-fillera PAS son configurateur sur le companion
   * à partir de ces properties — ça nécessiterait Selling Groups configurés côté
   * Customily. Ce qu'on fait ici est "best-effort" :
   *   - On garde la primaryText + Position en properties humaines pour que la
   *     production voie les chars à appliquer.
   *   - On marque le line item avec _inherited-from-companion pour traçabilité.
   *
   * Si tu actives un jour Customily Selling Groups, supprime cette logique et
   * laisse Customily gérer le sync via son propre flow.
   */
  function serializeForLineItem(design) {
    const props = {};

    // 1. PASS-THROUGH : on recopie toutes les properties humaines du business card,
    //    en strippant uniquement les patterns product-specific (Position/Layout).
    //    Les layers (ID 7-17) étant partagés entre business card et envelope,
    //    leurs properties (Text ID 12, Font ID 12, Color ID 12) peuvent être
    //    recopiées telles quelles — Customily de l'enveloppe les reconnaîtra
    //    si l'option set utilise les mêmes nom de fonctions.
    Object.entries(design.customization || {}).forEach(([key, value]) => {
      if (!value) return;
      if (STRIP_KEYS.has(key)) return;
      if (isStrippedHumanKey(key)) return;
      props[key] = value;
    });

    // 2. NOMS CANONIQUES pour la production : extraire les valeurs principales
    //    (chars/font/color) et les écrire sous des libellés lisibles, même si
    //    Customily les a nommées différemment dans son template.
    const fields = extractMonogramFields(design.customization || {});
    if (fields.text) props['Monogramme hérité'] = String(fields.text);
    if (fields.font) props['Police héritée'] = String(fields.font);
    if (fields.color) props['Couleur héritée'] = String(fields.color);

    // 3. Preview image Customily du produit principal
    if (design.previewUrl) {
      props['_inherited-preview'] = design.previewUrl;
    }

    // 4. Métadonnées de traçabilité production
    props['_inherited-from'] = 'pr-cross-sell-companion';
    if (design.personalizationId) {
      props['_inherited-personalization-id'] = design.personalizationId;
    }
    props['_inherited-note'] =
      'Personnalisation héritée du produit principal. Layers partagés (ID 7-17). Position ajustée selon ce produit.';

    return props;
  }

  /**
   * Helpers : persiste le design pour usage côté PDP (si client clique sur le
   * companion au lieu de "Ajouter", on peut pre-prompt côté configurateur).
   * Pas utilisé par défaut — point d'extension.
   */
  function persistForPDP(companionHandle, design) {
    try {
      sessionStorage.setItem(
        `pr_customily_inherit__${companionHandle}`,
        JSON.stringify(design)
      );
    } catch {}
  }

  function consumeForPDP(companionHandle) {
    try {
      const key = `pr_customily_inherit__${companionHandle}`;
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      sessionStorage.removeItem(key);
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  // --- Auto-suite : sync monogramme automatique entre TOUS les produits Customily ---
  // Quand le client personnalise n'importe quel produit Customily et l'ajoute au cart,
  // son design est stocké sous une clé sessionStorage GLOBALE. N'importe quelle PDP
  // Customily visitée ensuite peut récupérer ce design (peek, non-destructif).
  // Avantage : pas besoin de configurer un mapping par produit ni des tags suite.
  // La clé est overwritée à chaque nouvelle perso (le plus récent gagne).
  // Elle n'est clearée que sur action explicite "Recommencer" du client sur la notice.

  const SUITE_KEY = 'pr_monogram_suite_design';

  function persistForSuite(design) {
    try {
      sessionStorage.setItem(SUITE_KEY, JSON.stringify(design));
    } catch {}
  }

  function peekForSuite() {
    try {
      const raw = sessionStorage.getItem(SUITE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function clearForSuite() {
    try {
      sessionStorage.removeItem(SUITE_KEY);
    } catch {}
  }

  window.PRCustomilyBridge = {
    captureFromCart,
    renderPreview,
    serializeForLineItem,
    persistForPDP,
    consumeForPDP,
    persistForSuite,
    peekForSuite,
    clearForSuite,
    // Pour debug
    _internals: { META_KEYS, STRIP_KEYS, extractDesign, SUITE_KEY }
  };
})();

// rev: 1779209072 — force CDN cache bust
